import moment from 'moment';
import { getRealXAxisValue } from '@op/utils/chart';
import { normalizeOpinionDateType } from './getTimelineTimeFormat';
import type {
  IOpinionChartEventData,
  IOpinionChartEventSetting,
} from '../types';

function mapTimeKeyToChartXAxis(
  timeKey: string,
  xAxisData: string[],
  dateType?: string,
  format = 'YYYY-MM-DD',
): string | null {
  if (!timeKey || !xAxisData.length) return null;

  const normalizedDateType = normalizeOpinionDateType(dateType);

  // 留存等月粒度图表 x 轴为 YYYY-MM，event_timeline 为完整日期
  if (normalizedDateType === 'monthly') {
    const monthKey = moment(timeKey).isValid()
      ? moment(timeKey).format('YYYY-MM')
      : timeKey;
    if (xAxisData.includes(monthKey)) {
      return monthKey;
    }
  }
  const realKey = getRealXAxisValue({
    xAxis: timeKey,
    dateType: normalizedDateType,
    format,
    xAxisData,
  });

  if (xAxisData.includes(realKey)) {
    return realKey;
  }

  // 折线 x 轴可能是完整时间（如 2026-01-07 00:00:00），需与 event_timeline 的日期对齐
  const matchedAxis = xAxisData.find(item => {
    if (item === timeKey || item === realKey) {
      return true;
    }

    const normalizedItem = getRealXAxisValue({
      xAxis: item,
      dateType: normalizedDateType,
      format,
      xAxisData,
    });

    return normalizedItem === realKey;
  });

  return matchedAxis || null;
}

function mergeEventListBuckets(
  target: Record<string, any[]>,
  source: Record<string, any[]> = {},
) {
  Object.entries(source).forEach(([eventType, events]) => {
    if (!Array.isArray(events) || !events.length) return;
    if (!target[eventType]) {
      target[eventType] = [];
    }
    target[eventType].push(...events);
  });
}

export function remapChartRenderEventListToXAxis(
  chartRenderEventList: IOpinionChartEventData['chartRenderEventList'],
  xAxisData: string[],
  dateType?: string,
  format = 'YYYY-MM-DD',
): IOpinionChartEventData['chartRenderEventList'] {
  const result: IOpinionChartEventData['chartRenderEventList'] = {};

  Object.entries(chartRenderEventList || {}).forEach(([timeKey, payload]) => {
    const mappedKey = mapTimeKeyToChartXAxis(timeKey, xAxisData, dateType, format);
    if (!mappedKey || !payload) return;

    if (!result[mappedKey]) {
      result[mappedKey] = {
        count: 0,
        event_list: {},
      };
    }

    result[mappedKey].count += payload.count || 0;
    mergeEventListBuckets(result[mappedKey].event_list, payload.event_list);
  });

  return result;
}

export function remapBubbleEventToXAxis(
  bubbleEvent: Record<string, any[]>,
  xAxisData: string[],
  dateType?: string,
  format = 'YYYY-MM-DD',
): Record<string, any[]> {
  const result: Record<string, any[]> = {};

  Object.entries(bubbleEvent || {}).forEach(([timeKey, events]) => {
    const mappedKey = mapTimeKeyToChartXAxis(timeKey, xAxisData, dateType, format);
    if (!mappedKey || !Array.isArray(events) || !events.length) return;

    if (!result[mappedKey]) {
      result[mappedKey] = [];
    }
    result[mappedKey].push(...events);
  });

  return result;
}

export function remapOpinionEventStateToChartXAxis(
  eventData: IOpinionChartEventData,
  chartEventSetting: IOpinionChartEventSetting,
  xAxisData: string[],
  dateType?: string,
): {
  eventData: IOpinionChartEventData;
  chartEventSetting: IOpinionChartEventSetting;
} {
  const format = eventData?.format || 'YYYY-MM-DD';
  const remappedChartRenderEventList = remapChartRenderEventListToXAxis(
    eventData.chartRenderEventList,
    xAxisData,
    dateType,
    format,
  );
  const remappedBubbleEvent = remapBubbleEventToXAxis(
    chartEventSetting.bubbleEvent,
    xAxisData,
    dateType,
    format,
  );

  return {
    eventData: {
      ...eventData,
      chartRenderEventList: remappedChartRenderEventList,
    },
    chartEventSetting: {
      ...chartEventSetting,
      bubbleEvent: remappedBubbleEvent,
    },
  };
}
